import { Switch } from '@ads/components-react';
import type { ReactNode } from 'react';

/** Props de `PlanToggleBlock`. */
export interface PlanToggleBlockProps {
  /** Título do bloco. */
  readonly title: string;
  /** Explicação do que o interruptor habilita. */
  readonly hint: string;
  /** Estado do interruptor. */
  readonly checked: boolean;
  /** Recebe o novo estado. */
  readonly onCheckedChange: (checked: boolean) => void;
  /** Impede a troca de estado — usado quando a tela está somente leitura. */
  readonly disabled?: boolean | undefined;
  /**
   * Fundo do bloco.
   *
   * `body` (padrão) é o cinza de quem vive **dentro** de um card branco, como na
   * edição de plano — ali o contraste é o que separa o bloco da seção que o
   * contém. `surface` é o branco de quem é seção de primeiro nível, ao lado dos
   * outros cards, como na criação de parcela.
   */
  readonly tone?: 'body' | 'surface' | undefined;
  /** Campos do bloco; só renderizados quando ligado. */
  readonly children: ReactNode;
}

/**
 * Bloco de campos condicionado a um interruptor.
 *
 * Usado para "Boleto em nome da instituição" e "Pagamento de terceiro", que são
 * alternativas **paralelas** de responsável pela cobrança. Os dois usam este
 * mesmo componente de propósito: tratá-los de formas diferentes sugeriria uma
 * hierarquia entre eles que não existe no domínio.
 *
 * Os campos são desmontados quando o bloco está desligado — não apenas
 * escondidos — para que não haja campo inválido invisível bloqueando o
 * salvamento.
 */
export function PlanToggleBlock({
  title,
  hint,
  checked,
  onCheckedChange,
  disabled,
  tone = 'body',
  children,
}: PlanToggleBlockProps) {
  return (
    <div
      className={`flex flex-col rounded-sm border-hairline border-ctx-layout-border ${
        tone === 'surface' ? 'bg-ctx-layout-surface' : 'bg-ctx-layout-body'
      }`}
    >
      <div className="flex items-center gap-mini px-mini py-micro">
        <span className="flex flex-1 flex-col gap-quark">
          <span className="ts-paragraph-xs !font-semibold text-ctx-content-title">{title}</span>
          <span className="ts-paragraph-xxs text-ctx-content-placeholder">{hint}</span>
        </span>
        <Switch
          checked={checked}
          size="md"
          disabled={disabled}
          onCheckedChange={onCheckedChange}
          aria-label={title}
        />
      </div>
      {checked ? (
        <div className="flex flex-col gap-xxxs border-t-hairline border-ctx-layout-border px-mini py-xxxs">
          {children}
        </div>
      ) : null}
    </div>
  );
}
